/** @jsxImportSource @emotion/react */

import { lazy, Suspense, useState } from 'react';
import { useSelector } from 'react-redux';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import AvatarMui from '@mui/material/Avatar';
import LogoutIcon from '@mui/icons-material/Logout';

import Avatar from '../commons/Avatar';
import { colors } from '../../style/colors';
import { getUserSelector } from '../../models/factories/userFactories';
import useSignOut from '../../hooks/useSignOut';
import { CSSObject } from '@emotion/react';
import { breakpoints } from '../../style/breakpoints';
import { isPatientSelector } from '../../state-manager/selectors/appSelectors';

const TermsOfUseDialog = lazy(
  () =>
    import(
      /* webpackChunkName: "patient-terms" */ '../terms-of-use/TermsOfUseDialog'
    ),
);

const userMenuContainerCSS: CSSObject = {
  height: '100%',
  display: 'flex',
  '.MuiAvatar-root': {
    backgroundColor: colors.white,
    color: colors.blue1,
    width: 24,
    height: 24,
    fontSize: 12,

    [breakpoints.small]: {
      width: 34,
      height: 34,
      fontSize: 16,
    },
  },
};

const logOutIconCss: CSSObject = {
  width: 20,
  height: 20,

  [breakpoints.small]: {
    width: '1em',
    height: '1em',
  },
};

export default function UserMenu() {
  const user = useSelector(getUserSelector);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const signOut = useSignOut();
  const isPatient = useSelector(isPatientSelector);
  const [termsOpen, setTermsOpen] = useState(false);

  const openMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const closeMenu = () => {
    setAnchorEl(null);
  };

  return (
    <div css={userMenuContainerCSS}>
      <button onClick={openMenu}>
        {user ? (
          <Avatar user={user} />
        ) : (
          <AvatarMui>
            <LogoutIcon sx={logOutIconCss} />
          </AvatarMui>
        )}
      </button>
      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={closeMenu}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
      >
        {isPatient && (
          <MenuItem
            onClick={() => {
              setTermsOpen(true);
              closeMenu();
            }}
          >
            Terms of Use
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            signOut('user');
            closeMenu();
          }}
        >
          logout
        </MenuItem>
      </Menu>
      {termsOpen && (
        <Suspense fallback={null}>
          <TermsOfUseDialog open onClose={() => setTermsOpen(false)} />
        </Suspense>
      )}
    </div>
  );
}
